import React, { useEffect, useRef, useState } from 'react'
import { usePopper } from 'react-popper'
import { useCounter, useOutsideClick } from 'rooks'
import { Icon2 } from '@elamajs/ui-kit'

import { areDatesEqual, dateToRussianTimezone, getFormattedCalendarDate } from '../../helpers'

import * as Styled from './DatePicker.styled'

type Range = { min: Date | undefined; max: Date | undefined }

type Props = {
  value?: Range
  /** Доступный интервал фраз  */
  availableDateRange?: Range
  placeholder?: string
  disabled?: boolean
  onChange: (value: Range) => void
}

const MONTHS = [
  'Январь',
  'Февраль',
  'Март',
  'Апрель',
  'Май',
  'Июнь',
  'Июль',
  'Август',
  'Сентябрь',
  'Октябрь',
  'Ноябрь',
  'Декабрь'
]

const YEARS_PER_CALENDAR_PAGE = 2

/** Возвращает список годов, начиная от start включая end */
const generateDateInterval = (start: number, end: number): number[] =>
  Array.from({ length: end - start + 1 }, (_, i) => start + i)

const getIsRangeComplete = (value: Range | undefined): value is { [K in keyof Range]: Date } =>
  !!(value?.min && value?.max)

const getIsRangePartial = (
  value: Range | undefined
): value is { min: Date; max: undefined } | { min: undefined; max: Date } =>
  !!value && !getIsRangeComplete(value) && !!(value.min || value.max)

const generateNewValue = (currentValue: Range | undefined, date: Date): Range => {
  const isValuePartial = getIsRangePartial(currentValue)

  if (isValuePartial) {
    const partialValue = (currentValue?.min || currentValue?.max) as Date

    return {
      min: date < partialValue ? date : partialValue,
      max: date < partialValue ? partialValue : date
    }
  }

  return {
    min: date,
    max: undefined
  }
}

const DatePicker: React.FC<Props> = ({
  placeholder = 'Выберите',
  disabled,
  value: actualValue,
  availableDateRange,
  onChange
}) => {
  const popper = useRef<HTMLDivElement>(null)
  const reference = useRef<HTMLDivElement>(null)
  const [temporaryValue, setTemporaryValue] = useState<Range>()
  const [isOpen, setIsOpen] = useState(false)
  const [hoveredDate, setHoveredDate] = useState<Date | null>()
  const { value: page, increment: goToNextPage, decrement: goToPreviousPage } = useCounter(0)

  const { styles, attributes, update } = usePopper(reference.current, popper.current, {
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [0, 5]
        }
      },
      {
        name: 'preventOverflow',
        options: {
          altAxis: true,
          padding: { bottom: 45 + 18 }
        }
      }
    ]
  })

  useEffect(() => {
    setTemporaryValue(undefined)
  }, [actualValue])

  const value = temporaryValue ?? actualValue

  // Какой-то костыль, не помню что исправляет, кажется без него календарь рендерится в неправильном месте
  useEffect(() => {
    if (isOpen && update) {
      update()
    }
  }, [isOpen])

  const handleToggleSelect = () => {
    if (disabled) return

    setIsOpen(isOpen => !isOpen)
  }

  useOutsideClick(
    popper,
    e => {
      if (e.target === reference.current) return

      setIsOpen(false)
    },
    isOpen
  )

  const handleMonthHover = (date: Date) => {
    setHoveredDate(date)
  }

  const handleMonthHoverLeave = () => {
    setHoveredDate(null)
  }

  const handleSetValue = (date: Date) => {
    const newValue = generateNewValue(temporaryValue, date)

    const isValueComplete = getIsRangeComplete(newValue)

    if (isValueComplete) {
      onChange(newValue)
      setIsOpen(false)
    } else {
      setTemporaryValue(newValue)
    }
  }

  const isMonthDisabled = (date: Date) => {
    if (!availableDateRange) {
      return false
    }

    const { min, max } = availableDateRange

    return (min && date < min) || (max && date > max)
  }

  const isMonthSelected = (date: Date) =>
    (value?.min && areDatesEqual(date, value.min)) || (value?.max && areDatesEqual(date, value.max))

  const isMonthInBetween = (date: Date) =>
    getIsRangeComplete(value) && date > value.min && date < value.max

  const isMonthHovered = (date: Date) =>
    !!hoveredDate &&
    value?.min &&
    !areDatesEqual(value.min, date) &&
    !value?.max &&
    (areDatesEqual(date, hoveredDate) ||
      (hoveredDate < value.min
        ? date > hoveredDate && date < value.min
        : date < hoveredDate && date > value.min))

  const getMonthBorder = (date: Date) => {
    const isMinValue = value?.min && areDatesEqual(value.min, date)
    const isMaxValue = value?.max && areDatesEqual(value.max, date)

    if ((isMinValue && hoveredDate && hoveredDate < date) || (isMaxValue && value?.min)) {
      return Styled.MonthBorder.RIGHT
    }

    if ((isMinValue && hoveredDate && hoveredDate > date) || (isMinValue && value?.max)) {
      return Styled.MonthBorder.LEFT
    }

    return Styled.MonthBorder.BOTH
  }

  const getAvailableYearRange = () => {
    if (!availableDateRange || !availableDateRange.min || !availableDateRange.max) {
      // @TODO
      return []
    }

    const { min, max } = availableDateRange

    // Чтобы всегда на странице было по два года
    const availableInterval = generateDateInterval(
      min.getFullYear(),
      (max.getFullYear() - min.getFullYear()) % 2 === 1 ? max.getFullYear() : max.getFullYear() + 1
    )

    return availableInterval
  }

  const availableYearRange = getAvailableYearRange()

  const isSelected = getIsRangeComplete(actualValue)

  const formattedValue = [
    actualValue?.min
      ? getFormattedCalendarDate(dateToRussianTimezone(actualValue.min, true))
      : null,
    getIsRangeComplete(actualValue) && !areDatesEqual(actualValue.min, actualValue.max)
      ? getFormattedCalendarDate(dateToRussianTimezone(actualValue.max, true))
      : null
  ]
    .filter(val => !!val)
    .join(' - ')

  const currentPage = availableYearRange.slice(
    page * YEARS_PER_CALENDAR_PAGE,
    (page + 1) * YEARS_PER_CALENDAR_PAGE
  )

  const isAbleToGoToPreviousPage = page > 0
  const isAbleToGoToNextPage = page < availableYearRange.length / YEARS_PER_CALENDAR_PAGE - 1

  const isPaginationAvailable = isAbleToGoToNextPage || isAbleToGoToPreviousPage

  return (
    <>
      <Styled.Input
        ref={reference}
        isSelected={isSelected}
        isOpen={isOpen}
        disabled={disabled}
        onClick={handleToggleSelect}
      >
        <span>{formattedValue || placeholder} </span>
        <Icon2 icon="arrowDown" size="s" />
      </Styled.Input>
      <Styled.Container
        ref={popper}
        style={styles.popper}
        {...attributes.popper}
        isOpen={isOpen}
        isPaginationAvailable={isPaginationAvailable}
      >
        {isPaginationAvailable && (
          <Styled.PaginationContainer>
            <Styled.PaginationButton
              disabled={!isAbleToGoToPreviousPage}
              onClick={goToPreviousPage}
            >
              <Icon2 icon="arrowLeft" />
            </Styled.PaginationButton>
            <Styled.PaginationButton disabled={!isAbleToGoToNextPage} onClick={goToNextPage}>
              <Icon2 icon="arrowRight" />
            </Styled.PaginationButton>
          </Styled.PaginationContainer>
        )}
        {currentPage.map(year => (
          <Styled.Year key={year}>
            <Styled.Header>
              <span>{year}</span>
            </Styled.Header>
            <Styled.Body>
              {MONTHS.map((monthName, month) => {
                const date = dateToRussianTimezone(new Date(year, month, 1))

                return (
                  <Styled.Month
                    key={month}
                    isSelected={isMonthSelected(date)}
                    isInBetween={isMonthInBetween(date)}
                    isHovered={isMonthHovered(date)}
                    disabled={isMonthDisabled(date)}
                    monthBorder={getMonthBorder(date)}
                    onClick={() => handleSetValue(date)}
                    onMouseEnter={() => handleMonthHover(date)}
                    onMouseLeave={() => handleMonthHoverLeave()}
                  >
                    {monthName}
                  </Styled.Month>
                )
              })}
            </Styled.Body>
          </Styled.Year>
        ))}
      </Styled.Container>
    </>
  )
}

export default DatePicker
